import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Command } from "cmdk";
import { Film, Keyboard, LayoutTemplate, Newspaper, Plus, Settings, Trash2, UserRound, Users } from "lucide-react";
import { newspapersQuery } from "@/api/queries";
import { recentReels, useCanManage, useMe } from "@/lib/session";
import { useCurrentNewspaperId } from "./Shell";

const item =
  "flex h-9 cursor-default select-none items-center gap-2.5 rounded-md px-2.5 text-sm text-fg data-[selected=true]:bg-hover [&_svg]:size-4 [&_svg]:text-subtle";

export function CommandPalette({
  open,
  onOpenChange,
  onShortcuts,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onShortcuts: () => void;
}) {
  const navigate = useNavigate();
  const me = useMe();
  const np = useCurrentNewspaperId();
  const canManage = useCanManage(np);
  const { data: papers = [] } = useQuery(newspapersQuery);
  const go = (fn: () => void) => {
    onOpenChange(false);
    fn();
  };
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/30" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-[14vh] z-50 w-[calc(100vw-32px)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-surface shadow-overlay"
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">Go to</DialogPrimitive.Title>
          <Command label="Go to" loop>
            <Command.Input
              autoFocus
              placeholder="Type a command or search…"
              className="h-12 w-full border-b border-border bg-transparent px-4 text-base outline-none placeholder:text-subtle"
            />
            <Command.List className="max-h-[50vh] overflow-y-auto p-1.5">
              <Command.Empty className="px-3 py-6 text-center text-sm text-muted">Nothing found.</Command.Empty>
              {np && (
                <Command.Group heading="Actions" className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-subtle">
                  <Command.Item className={item} onSelect={() => go(() => navigate({ to: "/n/$npId/reels", params: { npId: np }, search: { new: true } }))}>
                    <Plus /> New reel
                  </Command.Item>
                </Command.Group>
              )}
              {recentReels().length > 0 && (
                <Command.Group heading="Recent reels" className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-subtle">
                  {recentReels().map((r) => (
                    <Command.Item key={r.id} value={`reel ${r.label}`} className={item} onSelect={() => go(() => navigate({ to: "/reels/$reelId", params: { reelId: r.id }, search: {} }))}>
                      <Film /> <span className="font-content">{r.label}</span>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
              {np && (
                <Command.Group heading="Go to" className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-subtle">
                  <Command.Item className={item} onSelect={() => go(() => navigate({ to: "/n/$npId/reels", params: { npId: np } }))}>
                    <Film /> Reels
                  </Command.Item>
                  {canManage && (
                    <>
                      <Command.Item className={item} onSelect={() => go(() => navigate({ to: "/n/$npId/templates", params: { npId: np } }))}>
                        <LayoutTemplate /> Templates
                      </Command.Item>
                      <Command.Item className={item} onSelect={() => go(() => navigate({ to: "/n/$npId/people", params: { npId: np } }))}>
                        <Users /> People
                      </Command.Item>
                      <Command.Item className={item} onSelect={() => go(() => navigate({ to: "/n/$npId/settings/$tab", params: { npId: np, tab: "brand" } }))}>
                        <Settings /> Newspaper settings
                      </Command.Item>
                    </>
                  )}
                  <Command.Item className={item} onSelect={() => go(() => navigate({ to: "/n/$npId/trash", params: { npId: np } }))}>
                    <Trash2 /> Trash
                  </Command.Item>
                  <Command.Item className={item} onSelect={() => go(() => navigate({ to: "/account" }))}>
                    <UserRound /> Account
                  </Command.Item>
                  {me.role === "admin" && (
                    <Command.Item className={item} onSelect={() => go(() => navigate({ to: "/admin/members" }))}>
                      <Users /> Everyone & invites
                    </Command.Item>
                  )}
                  <Command.Item className={item} onSelect={() => go(onShortcuts)}>
                    <Keyboard /> Keyboard shortcuts
                  </Command.Item>
                </Command.Group>
              )}
              {papers.length > 1 && (
                <Command.Group heading="Switch newspaper" className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-subtle">
                  {papers.map((p) => (
                    <Command.Item key={p.id} value={`newspaper ${p.name}`} className={item} onSelect={() => go(() => navigate({ to: "/n/$npId/reels", params: { npId: p.id } }))}>
                      <Newspaper /> {p.name}
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
            </Command.List>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
